import React, { useEffect, useState } from "react";
import useModuleSubscription from "@/hooks/useModuleSubscription";
import { Tab, Tabs, TabList, TabPanel, TabPanels } from "@chaser/components";
import { getRoles } from "@/components/FeatureGate/RoleGate";
import { roles, AVAILABLE_ROLES, isGovernanceRole } from "@/util/constants";
import useShortCode from "@/hooks/useShortCode";
import useCurrentUserRoles from "@/hooks/useCurrentUserRoles";
import useModal from "@/hooks/useModal";
import RoleCard from "./RoleCard";
import RoleUsersModal from "./RoleUsersModal";
import styles from "./Roles.module.scss";

const MANAGEMENT_ROLES = [roles.owner, roles.manager, roles.billing];
const RECEIVABLES_ROLES = [
  roles.receivablesManager,
  roles.receivablesContributor,
  roles.receivablesViewer,
];
const CASHFLOW_ROLES = [roles.cashflowManager];

function countUsersByRole(users, shortCode) {
  const counts = {};
  AVAILABLE_ROLES.forEach((role) => {
    counts[role] = 0;
  });

  const { chaserUsers = [], invitedChaserUsers = [] } = users;

  chaserUsers.forEach((user) => {
    const { organisationRoles = [] } = getRoles(user, shortCode);
    organisationRoles.forEach((role) => {
      if (counts[role] !== undefined) {
        counts[role]++;
      }
    });
  });

  invitedChaserUsers.forEach((user) => {
    const userRoles = user.roles || [];
    userRoles.forEach((role) => {
      if (counts[role] !== undefined) {
        counts[role]++;
      }
    });
  });

  return counts;
}

const RoleSection = ({
  rolesList,
  counts,
  onViewUsers,
  onManageUsers,
  manageDisabledRoles,
}) => (
  <div className={styles.grid}>
    {rolesList.map((role) => (
      <RoleCard
        key={role}
        role={role}
        userCount={counts[role]}
        onViewUsers={onViewUsers}
        onManageUsers={onManageUsers}
        manageDisabled={manageDisabledRoles.includes(role)}
      />
    ))}
  </div>
);

const SECTIONS = [
  {
    id: "management",
    label: "Management",
    rolesList: MANAGEMENT_ROLES,
    alwaysVisible: true,
  },
  {
    id: "receivables",
    label: "Receivables",
    rolesList: RECEIVABLES_ROLES,
    alwaysVisible: true,
  },
  {
    id: "cashflow-intelligence",
    label: "Cash Flow Intelligence",
    rolesList: CASHFLOW_ROLES,
    alwaysVisible: false,
  },
];

const Roles = ({ users, applyOptimisticUpdate }) => {
  const shortCode = useShortCode();
  const { hasModule: hasCashflow } = useModuleSubscription("cashflow-module");
  const counts = countUsersByRole(users, shortCode);
  const { isStaff, isOwner, isManager } = useCurrentUserRoles();
  const showModal = useModal();

  const manageDisabledRoles = AVAILABLE_ROLES.filter((role) => {
    if (isStaff || isOwner) return false;
    if (isManager) return isGovernanceRole(role);
    return true;
  });

  const handleViewUsers = (role) => {
    showModal(
      <RoleUsersModal
        role={role}
        initialMode="current"
        users={users}
        applyOptimisticUpdate={applyOptimisticUpdate}
        manageDisabled={manageDisabledRoles.includes(role)}
      />,
    );
  };

  const handleManageUsers = (role) => {
    showModal(
      <RoleUsersModal
        role={role}
        initialMode="manage"
        users={users}
        applyOptimisticUpdate={applyOptimisticUpdate}
      />,
    );
  };

  const visibleSections = SECTIONS.filter(
    (s) => s.alwaysVisible || hasCashflow,
  );
  const [activeSectionId, setActiveSectionId] = useState(
    visibleSections[0]?.id || null,
  );

  useEffect(() => {
    if (!visibleSections.length) return;

    const activeStillVisible = visibleSections.some(
      (section) => section.id === activeSectionId,
    );
    if (!activeStillVisible) {
      setActiveSectionId(visibleSections[0].id);
    }
  }, [activeSectionId, visibleSections]);

  return (
    <div className={styles.container}>
      <Tabs>
        <TabList>
          {visibleSections.map((s) => (
            <Tab key={s.label}>{s.label}</Tab>
          ))}
        </TabList>
        <TabPanels>
          {visibleSections.map((s) => (
            <TabPanel key={s.label}>
              <RoleSection
                rolesList={s.rolesList}
                counts={counts}
                onViewUsers={handleViewUsers}
                onManageUsers={handleManageUsers}
                manageDisabledRoles={manageDisabledRoles}
              />
            </TabPanel>
          ))}
        </TabPanels>
      </Tabs>
    </div>
  );
};

export default Roles;
